import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/Header";
import { RepurposeModal } from "@/components/RepurposeModal";
import { ApprovalLinkModal } from "@/components/ApprovalLinkModal";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { STATUS_CONFIG } from "@/lib/generateConstants";

import { DraftRecoveryBanner } from "@/components/history/DraftRecoveryBanner";
import { HistoryFilters } from "@/components/history/HistoryFilters";
import { HistoryCard } from "@/components/history/HistoryCard";
import { ScoreModal } from "@/components/history/ScoreModal";
import { HistoryViewModal } from "@/components/history/HistoryViewModal";
import { DraftPreviewModal } from "@/components/history/DraftPreviewModal";
import { useHistoryActions } from "@/hooks/useHistoryActions";

export default function HistoryPage() {
  const { api, user } = useAuth();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterPlatform, setFilterPlatform] = useState("");
  const [filterMode, setFilterMode] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [viewItem, setViewItem] = useState(null);
  const [availableModels, setAvailableModels] = useState([]);
  const [drafts, setDrafts] = useState([]);

  const isClient = user?.business_role === "client";

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: "20" });
      if (search) params.set("search", search);
      if (filterType) params.set("content_type", filterType);
      if (filterPlatform) params.set("platform", filterPlatform);
      if (filterMode) params.set("filter_mode", filterMode);
      if (filterStatus) params.set("status", filterStatus);
      const resp = await api.get(`/history?${params.toString()}`);
      setItems(resp.data.items);
      setTotal(resp.data.total);
      setPages(resp.data.pages);
    } catch {
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  }, [api, page, search, filterType, filterPlatform, filterMode, filterStatus]);

  const fetchDrafts = useCallback(async () => {
    try {
      const resp = await api.get("/history/drafts");
      setDrafts(resp.data.drafts || []);
    } catch (e) { console.error("Failed to fetch drafts:", e); }
  }, [api]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);
  useEffect(() => { fetchDrafts(); }, [fetchDrafts]);

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const resp = await api.get("/models/available");
        setAvailableModels(resp.data.models || []);
      } catch (e) { console.error("Failed to fetch models:", e); }
    };
    fetchModels();
  }, [api]);

  const actions = useHistoryActions({ api, fetchHistory });

  // Close status dropdown on outside click
  useEffect(() => {
    if (!actions.statusDropdownOpen) return;
    const close = () => actions.setStatusDropdownOpen(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [actions.statusDropdownOpen, actions]);

  // Bridge handlers that update local items state
  const onToggleFavorite = async (item) => {
    const result = await actions.handleToggleFavorite(item);
    if (result) setItems((prev) => prev.map((i) => i.history_id === item.history_id ? { ...i, is_favorited: result.is_favorited } : i));
  };

  const onTogglePin = async (item) => {
    await actions.handleTogglePin(item);
  };

  const onStatusChange = async (item, newStatus) => {
    setItems((prev) => prev.map((i) => i.history_id === item.history_id ? { ...i, status: newStatus } : i));
    const result = await actions.handleStatusChange(item, newStatus);
    if (!result) setItems((prev) => prev.map((i) => i.history_id === item.history_id ? { ...i, status: item.status || "draft" } : i));
  };

  const onPublishDate = async (item, dateStr) => {
    const val = dateStr || null;
    setItems((prev) => prev.map((i) => i.history_id === item.history_id ? { ...i, planned_publish_date: val } : i));
    const result = await actions.handlePublishDate(item, dateStr);
    if (result === undefined) setItems((prev) => prev.map((i) => i.history_id === item.history_id ? { ...i, planned_publish_date: item.planned_publish_date } : i));
  };

  const onScoreContent = async (historyId) => {
    const result = await actions.scoreContent(historyId);
    if (result) {
      setItems((prev) => prev.map((i) => i.history_id === historyId
        ? { ...i, quality_score: result.quality_score, score_breakdown: result.score_breakdown, score_feedback: result.score_feedback, scored_at: result.scored_at }
        : i));
    }
  };

  const onDiscardDraft = async (draftId) => {
    const ok = await actions.handleDiscardDraft(draftId);
    if (ok) setDrafts((prev) => prev.filter((d) => d.draft_id !== draftId));
  };

  const getRepurposeModel = (item) => {
    if (!item) return availableModels[0] || "";
    if (availableModels.includes(item.ai_model_used)) return item.ai_model_used;
    return availableModels[0] || "";
  };

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="history-page">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-bold text-[var(--text-primary)] tracking-tight">History</h1>
          <span className="text-sm text-[var(--text-muted)]">{total} generation{total !== 1 ? "s" : ""}</span>
        </div>

        <HistoryFilters
          search={search} onSearchChange={(v) => { setSearch(v); setPage(1); }}
          filterType={filterType} onFilterTypeChange={(v) => { setFilterType(v); setPage(1); }}
          filterPlatform={filterPlatform} onFilterPlatformChange={(v) => { setFilterPlatform(v); setPage(1); }}
          filterMode={filterMode} onFilterModeChange={(v) => { setFilterMode(v); setPage(1); }}
          filterStatus={filterStatus} onFilterStatusChange={(v) => { setFilterStatus(v); setPage(1); }}
        />

        <DraftRecoveryBanner drafts={drafts} onResume={actions.handleResumeDraft} onPreview={actions.setDraftPreview} onDiscard={onDiscardDraft} />

        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (<div key={`skeleton-${i}`} className="h-20 bg-[var(--bg-card)] rounded-lg animate-pulse" />))}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16" data-testid="history-empty">
            <p className="text-[var(--text-muted)] text-lg">{filterMode ? `No ${filterMode} items` : filterStatus ? `No ${STATUS_CONFIG[filterStatus]?.label || filterStatus} items` : "No saved generations yet"}</p>
            <p className="text-[var(--text-muted)] text-sm mt-2">{filterMode ? "Try removing the filter" : "Generate some content and save it to see it here"}</p>
          </div>
        ) : (
          <div className="space-y-3">
            <AnimatePresence>
              {items.map((item, idx) => (
                <HistoryCard key={item.history_id} item={item} idx={idx} isClient={isClient}
                  statusDropdownOpen={actions.statusDropdownOpen}
                  onStatusDropdownToggle={(id) => actions.setStatusDropdownOpen(actions.statusDropdownOpen === id ? null : id)}
                  onStatusChange={onStatusChange} onPublishDate={onPublishDate}
                  onTogglePin={onTogglePin} onToggleFavorite={onToggleFavorite}
                  onScore={onScoreContent} scoringId={actions.scoringId}
                  onView={setViewItem} onEdit={actions.loadToEditor}
                  onRepurpose={actions.setRepurposeItem} onDuplicate={actions.handleDuplicate}
                  onShare={(item) => actions.setApprovalModal({ ids: [item.history_id], title: `${item.title || item.topic} — Review` })}
                  onDelete={actions.setDeleteId}
                />
              ))}
            </AnimatePresence>
          </div>
        )}

        {pages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-8">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="p-2.5 sm:p-2 rounded-md text-[var(--text-secondary)] hover:text-[var(--text-primary)] disabled:opacity-30 transition-colors" data-testid="history-prev-page"><CaretLeft size={20} /></button>
            <span className="text-sm text-[var(--text-secondary)]">Page {page} of {pages}</span>
            <button onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page === pages} className="p-2.5 sm:p-2 rounded-md text-[var(--text-secondary)] hover:text-[var(--text-primary)] disabled:opacity-30 transition-colors" data-testid="history-next-page"><CaretRight size={20} /></button>
          </div>
        )}

        <HistoryViewModal viewItem={viewItem} onClose={() => setViewItem(null)} onRepurpose={(item) => { actions.setRepurposeItem(item); setViewItem(null); }} />

        <Dialog open={!!actions.deleteId} onOpenChange={() => actions.setDeleteId(null)}>
          <DialogContent className="bg-[var(--bg-raised)] border-[var(--border-strong)] text-[var(--text-primary)]">
            <DialogHeader><DialogTitle className="text-[var(--text-primary)]">Delete this generation?</DialogTitle></DialogHeader>
            <p className="text-[var(--text-secondary)] text-sm">Are you sure? This cannot be undone.</p>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => actions.setDeleteId(null)} className="border-[var(--border-strong)] text-[var(--text-secondary)]" data-testid="delete-cancel">Cancel</Button>
              <Button onClick={actions.handleDelete} className="bg-red-500 hover:bg-red-600 text-white" data-testid="delete-confirm">Delete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <RepurposeModal isOpen={!!actions.repurposeItem} onClose={() => actions.setRepurposeItem(null)}
          originalContent={actions.repurposeItem?.generated_draft || ""} originalType={actions.repurposeItem?.content_type || ""}
          originalPlatform={actions.repurposeItem?.platform || ""} audience={actions.repurposeItem?.audience || ""}
          availableModels={availableModels} defaultTones={actions.repurposeItem?.tone_variations || ["Direct", "Practical"]}
          defaultModel={getRepurposeModel(actions.repurposeItem)} />

        <DraftPreviewModal draftPreview={actions.draftPreview} onClose={() => actions.setDraftPreview(null)}
          onResume={(draft) => { actions.setDraftPreview(null); actions.handleResumeDraft(draft); }}
          onSave={actions.handleSaveDraftToHistory} savingDraft={actions.savingDraft} />
      </main>

      {actions.approvalModal && (
        <ApprovalLinkModal open={!!actions.approvalModal} onOpenChange={(open) => !open && actions.setApprovalModal(null)}
          historyItemIds={actions.approvalModal.ids} defaultTitle={actions.approvalModal.title} />
      )}

      <ScoreModal scoreModal={actions.scoreModal} onClose={() => actions.setScoreModal(null)} />
    </div>
  );
}
